/* ============================================================
   BNE STUDIO — Live (Midnight Blueprint)
   Recreated from the design handoff:
   "BNE Studio - Live.html". Renders inside NightLayout
   (shared nav/footer + motion engine + scoped design system).
   Placeholder CTAs / login / external links point to "#";
   cross-page links route within the app. Page lives at /live.
   ============================================================ */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NightLayout from "../components/night/NightLayout";
import "../styles/night-live.css";

// arrow used by the primary CTAs
const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const LivePage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "BNE Live — Put the show on sale";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <NightLayout description="Launch tickets in minutes and watch the room sell out." active="live">
      <div className="live-wash" />

      {/* HERO */}
      <header className="phero" style={{ "--c": "var(--p-live)" }}>
        <div className="wrap">
          <div className="phero-grid">
            <div className="reveal">
              <div className="ico-badge">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2 2 2 0 000 4 2 2 0 000 4 2 2 0 01-2 2H6a2 2 0 01-2-2 2 2 0 000-4 2 2 0 000-4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="eyebrow" style={{ marginBottom: "16px" }}>Perform</span>
              <h1 className="grad-text">Sell out every show.</h1>
              <p className="lead">Launch tickets in minutes and watch the room sell out.</p>
              <div className="ctas">
                <a className="btn btn-primary btn-lg btn-arrow" href="https://bne.live">
                  Create an Event <Arrow />
                </a>
                <span className="domain">bne.live</span>
              </div>
            </div>
            <div className="reveal" data-d="1">
              <div className="big-mock">
                <div className="bm-bar">
                  <i />
                  <i />
                  <i />
                  <span className="t">bne.live / neon-hours-live</span>
                  <span className="pill">On sale</span>
                </div>
                <div className="bm-body">
                  <div className="poster">
                    <span className="tag">Fri · 9PM · The Warehouse</span>
                    <div className="qr">
                      <span />
                      <span style={{ background: "transparent" }} />
                      <span />
                      <span />
                      <span style={{ background: "transparent" }} />
                      <span />
                      <span style={{ background: "transparent" }} />
                      <span />
                      <span />
                      <span />
                      <span style={{ background: "transparent" }} />
                      <span />
                      <span />
                      <span style={{ background: "transparent" }} />
                      <span />
                      <span />
                    </div>
                    <h2 style={{ fontSize: "30px", color: "#fff", letterSpacing: "-0.03em" }}>Neon Hours Live</h2>
                  </div>
                  <div className="tier">
                    <b>General Admission</b>
                    <span className="px">$24</span>
                  </div>
                  <div className="tier">
                    <b>Early Entry + Print</b>
                    <span className="px">$42</span>
                  </div>
                  <div className="tier sold">
                    <b>Front Row · sold out</b>
                    <span className="px">$65</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px", padding: "0 2px" }}>
                    <span style={{ fontFamily: "var(--disp)", fontWeight: 700, fontSize: "18px", color: "var(--heading)" }}>
                      <span data-count="312">0</span> <span style={{ fontSize: "12px", color: "var(--fog)", fontWeight: 500 }}>/ 400 sold</span>
                    </span>
                    <span className="chip" style={{ color: "var(--ok)" }}>78% to sellout</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* OUTCOMES */}
      <section className="section tight" style={{ "--c": "var(--p-live)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">What it helps you do</span>
            <h2>From event page to entry door.</h2>
          </div>
          <div className="outcomes">
            <div className="outcome reveal" data-d="1">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 8h16M3 4h18a1 1 0 011 1v1a2 2 0 01-2 2H4a2 2 0 01-2-2V5a1 1 0 011-1zM4 8v10a1 1 0 001 1h14a1 1 0 001-1V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Build the event page</h4>
              <p>Set the venue, date, lineup, and ticket tiers — and put it on sale in minutes.</p>
            </div>
            <div className="outcome reveal" data-d="2">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M3 10h18M7 3v4M17 3v4M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Sell &amp; track tickets</h4>
              <p>Multiple tiers, real-time sales, and a clear view of how the room is filling up.</p>
            </div>
            <div className="outcome reveal" data-d="3">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M5 7h2v2H5zM5 11h2v2H5zM9 7h2v2H9zM9 11h2v2H9zM13 7h6v6h-6zM5 15h14v2H5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Manage entry</h4>
              <p>QR tickets and a guest list that make the door fast on the night.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="section" style={{ "--c": "var(--p-live)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">How it works</span>
            <h2>Set it up, sell it out, run the door.</h2>
          </div>
          <div className="work-split">
            <div className="vsteps reveal">
              <div className="vstep">
                <div className="n">1</div>
                <div>
                  <h4>Create the event</h4>
                  <p>Venue, date, lineup, and ticket tiers all on one page.</p>
                </div>
              </div>
              <div className="vstep">
                <div className="n">2</div>
                <div>
                  <h4>Put it on sale</h4>
                  <p>Share the link, watch sales come in, and adjust tiers on the fly.</p>
                </div>
              </div>
              <div className="vstep">
                <div className="n">3</div>
                <div>
                  <h4>Run the night</h4>
                  <p>Scan QR tickets, check the guest list, and let people in.</p>
                </div>
              </div>
            </div>
            <div className="mock reveal" data-d="1">
              <div className="mock-bar">
                <i />
                <i />
                <i />
                <span className="t">bne.live / neon-hours-live</span>
                <span className="pill">On sale</span>
              </div>
              <div className="mock-body">
                <div className="statgrid">
                  <div className="s">
                    <div className="k">Sold today</div>
                    <div className="v" data-count="312">0</div>
                  </div>
                  <div className="s">
                    <div className="k">Capacity</div>
                    <div className="v">400</div>
                  </div>
                  <div className="s">
                    <div className="k">Revenue</div>
                    <div className="v">$6.2k</div>
                  </div>
                </div>
                <div className="row">
                  <span className="m">
                    <b>General Admission</b>
                    <span>$20 · 240 sold</span>
                  </span>
                  <span className="chip-status st-live">On sale</span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>Early Bird</b>
                    <span>$15 · sold out</span>
                  </span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--fog)" }}>Sold out</span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>VIP + Meet</b>
                    <span>$60 · 12 left</span>
                  </span>
                  <span className="chip-status st-q">Selling</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="section tight" style={{ "--c": "var(--p-live)" }}>
        <div className="wrap">
          <div className="proof">
            <div className="pc reveal" data-d="1">
              <div className="lab">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M3 3v18h18M7 14l3-3 3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Sales
              </div>
              <h4>Watch the room fill up.</h4>
              <p>Real-time sales by tier, so you know when to push promotion and when to add capacity.</p>
              <div className="statgrid" style={{ marginTop: "4px" }}>
                <div className="s">
                  <div className="k">Sold</div>
                  <div className="v" data-count="312">0</div>
                </div>
                <div className="s">
                  <div className="k">Revenue</div>
                  <div className="v">$<span data-count="8.4" data-dec="1">0</span>K</div>
                </div>
                <div className="s">
                  <div className="k">To sellout</div>
                  <div className="v">88</div>
                </div>
              </div>
            </div>
            <div className="pc reveal" data-d="2">
              <div className="lab">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M5 7h4v4H5zM15 7h4v4h-4zM5 15h4v4H5zM15 15h2M19 15v4M15 19h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Entry
              </div>
              <h4>A fast door on the night.</h4>
              <p>Every ticket carries a QR code, and the guest list updates live as people walk in.</p>
              <div style={{ marginTop: "4px" }}>
                <div className="row">
                  <span className="m">
                    <b>Ava + 1</b>
                    <span>Early Entry</span>
                  </span>
                  <span className="chip-status st-live">Checked in</span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>Marcus T.</b>
                    <span>General Admission</span>
                  </span>
                  <span className="chip-status st-live">Checked in</span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>Priya N.</b>
                    <span>Front Row</span>
                  </span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--fog)" }}>expected</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="section" style={{ "--c": "var(--p-live)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">Connected to the rest of BNE</span>
            <h2>The show is part of the rollout.</h2>
          </div>
          <div className="eco-grid">
            <Link className="eco reveal" data-d="1" style={{ "--c": "var(--p-sync)" }} to="/sync">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 6a2 2 0 012-2h4l2 2.5h6a2 2 0 012 2V18a2 2 0 01-2 2H6a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Sync</b>
              <p>Keep the show's assets in one project.</p>
            </Link>
            <Link className="eco reveal" data-d="2" style={{ "--c": "var(--p-dist)" }} to="/distribute">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Distribution</b>
              <p>Time the show to a release.</p>
            </Link>
            <Link className="eco reveal" data-d="3" style={{ "--c": "var(--p-vision)" }} to="/vision">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3ZM3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Vision</b>
              <p>Make a reel to promote the date.</p>
            </Link>
            <Link className="eco reveal" data-d="4" style={{ "--c": "var(--p-contracts)" }} to="/contracts">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M14 3v4a1 1 0 0 0 1 1h4M18 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8l6 6v3M8 18c1.4-2 2.8-2 4 0s2.6 2 4 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Contracts</b>
              <p>Paper the booking and the deal.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section tight">
        <div className="wrap">
          <div className="cta-band reveal-scale">
            <div className="glow" />
            <span className="eyebrow center" style={{ justifyContent: "center", marginBottom: "20px" }}>Perform · Live</span>
            <h2 className="grad-text">Take it into the room.</h2>
            <p>Create the event, sell the tickets, and bring the release to a real crowd.</p>
            <div className="row" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <a className="btn btn-primary btn-lg btn-arrow" href="https://bne.live">
                Create an Event <Arrow />
              </a>
              <Link className="btn btn-ghost btn-lg" to="/home">
                Explore the Ecosystem
              </Link>
            </div>
          </div>
        </div>
      </section>
    </NightLayout>
  );
};

export default LivePage;
